/**
 * reportScope — separa ORÇAMENTOS de VENDAS DO BALCÃO (PDV).
 *
 * ─── O BUG QUE ISTO CORRIGE ─────────────────────────────────────────────────
 *
 * A Venda Rápida grava a venda na tabela `quotes`, com `document_type='pedido'`
 * e `status='completed'` (ver `quickSale.ts`). O Relatório de Orçamentos lia
 * `quotes` da empresa no período **sem filtrar `document_type`**, então toda
 * venda de balcão entrava no relatório de orçamentos: inflava "Vendido",
 * "Aprovados" e "Ticket médio", contaminava o gráfico de faturamento, a pizza de
 * status e o "Top clientes" (aparecendo como "Consumidor não identificado").
 *
 * Que isso era inconsistência e não decisão está provado no próprio app: o
 * `DashboardPage` sempre excluiu (`.neq('document_type', 'pedido')`) e a
 * `QuotesListPage` também (`quote.document_type !== 'pedido'`). Só o relatório
 * não filtrava.
 *
 * ─── POR QUE PARTIÇÃO, E NÃO UM FILTRO NO SERVIDOR ──────────────────────────
 *
 * Simplesmente tirar o PDF do SQL deixaria a receita do balcão **invisível** em
 * /relatorios — trocaria um número inflado por dinheiro que some. A leitura traz
 * as duas coisas e esta função as separa: os orçamentos alimentam os KPIs, e as
 * vendas do PDV têm card próprio, rotulado como fora deles.
 *
 * ⚠️ E por que o predicado é `=== 'pedido'` e não `!== 'pedido'` invertido no
 * SQL: `document_type` é NULLABLE. Em SQL, `document_type <> 'pedido'` é NULL
 * (não `true`) para uma linha sem tipo, então um `.neq()` sozinho **descartaria
 * os orçamentos legados** do relatório, sem erro nenhum. Aqui a regra é positiva
 * — só quem É 'pedido' sai —, e por isso `null` continua sendo orçamento.
 */
import { isSaleDocument } from '@/lib/quickSale';

/** O mínimo que a partição precisa de uma linha de `quotes`. */
export interface ReportQuoteLike {
  document_type?: string | null;
  total?: number | null;
  status?: string | null;
}

export interface PdvSalesSummary {
  count: number;
  total: number;
}

export interface QuotesReportScope<T extends ReportQuoteLike> {
  /** Orçamentos de verdade — o que alimenta os KPIs da aba Orçamentos. */
  quotes: T[];
  /** Vendas do balcão, contadas à parte e mostradas em card próprio. */
  pdvSales: T[];
  pdvSummary: PdvSalesSummary;
}

/**
 * Separa a lista em orçamentos × vendas do PDV.
 *
 * Usa `isSaleDocument`, a MESMA função da lista de orçamentos e do resumo do
 * cliente — uma segunda definição de "é venda de balcão?" faria as telas
 * discordarem sobre o que é um orçamento.
 */
export function partitionQuotesForReport<T extends ReportQuoteLike>(
  rows: readonly T[] | null | undefined,
): QuotesReportScope<T> {
  const quotes: T[] = [];
  const pdvSales: T[] = [];
  let total = 0;

  for (const row of rows ?? []) {
    if (isSaleDocument(row)) {
      pdvSales.push(row);
      const value = Number(row?.total ?? 0);
      total += Number.isFinite(value) ? value : 0;
    } else {
      quotes.push(row);
    }
  }

  return { quotes, pdvSales, pdvSummary: { count: pdvSales.length, total } };
}
